import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api.ts";
import { getExpiryInfo, downloadGatePassAsPDF } from "../../utils/functions.js";
import {
  FileText,
  Download,
  Eye,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  X,
  Printer,
  Loader,
  Ticket,
} from "lucide-react";
import {
  downloadGatePassPdf,
  streamGatePassPdf,
  getBuyerGatePasses,
  getSellerGatePasses,
} from "../../services/gatePassService.ts";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import GatePassFromImage from "./GatePassPopup";
import Pagination from "../common/Pagination.jsx";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import dayjs from "dayjs";
import skyautologo from "../../assets/skyAutologo.png"
import expirePng from "../../assets/expire.png"
import approvedpng from "../../assets/approved.png"
import { generateGatePassPdf } from "../../utils/gatePassPdf";

const BuyGatePasses = () => {
  const { translate, language } = useLanguage();
  const { user } = useAuth();
  const [gatePasses, setGatePasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all, issued, pending, expired
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showGatePassModal, setShowGatePassModal] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [streamingId, setStreamingId] = useState(null);

  // Currency utils – convert from AED + display with symbol
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  const [childBuyers, setChildBuyers] = useState([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState(null);

  const selectedChildBuyer = React.useMemo(() => {
    if (!selectedBuyerId) return null;
    return childBuyers.find((child) => child.buyerId === selectedBuyerId) || null;
  }, [childBuyers, selectedBuyerId]);

  const selectedBuyerDisplayName =
    selectedChildBuyer?.user?.name || user?.name || "";
  const selectedBuyerDisplayClientId =
    selectedChildBuyer?.user?.clientId || user?.clientId || "";

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    type: "buyer",
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Fetch child buyers (corporate buyer dropdown)
  useEffect(() => {
    const fetchChildBuyers = async () => {
      try {
        const response = await api.get("/users/corporatebuyers", {
          params: { limit: 100 },
        });
        const data = response.data?.data || [];
        setChildBuyers(data);
      } catch (error) {
        console.debug("Unable to load corporate child buyers:", error?.message);
        setChildBuyers([]);
      }
    };

    fetchChildBuyers();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus]);

  // Fetch gate passes from API
  useEffect(() => {
    const fetchGatePasses = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user?.id) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }
        const params = {
          page: currentPage,
          limit: pageSize,
          sortBy: "id",
          sortOrder: "desc",
          filters: {},
        };

        if (filterStatus !== "all") {
          params.filters.validityStatus = filterStatus;
        }

        if (debouncedSearch && debouncedSearch.trim() !== "") {
          params.search = debouncedSearch.trim();
        }

        let response = null;

        const currentBuyerId = selectedBuyerId || user?.buyer?.id;

        if (user?.buyer?.id) {
          response = await getBuyerGatePasses(currentBuyerId, params);
        } else {
          response = await getSellerGatePasses(user?.seller?.id, params);
        }

        if (response?.pagination) {
          setPagination({
            currentPage: response.pagination.page,
            totalPages: response.pagination.totalPages,
            totalItems: response.pagination.total,
            itemsPerPage: response.pagination.limit,
          });
        }

        const gatePassesData = response.data || response || [];

        if (!Array.isArray(gatePassesData)) {
          console.error("API response data is not an array:", gatePassesData);
          setGatePasses([]);
          return;
        }

        setGatePasses(gatePassesData);
        // hide the gatepasses whose status is cancelled 
//         setGatePasses(
//   gatePassesData.filter(
//     (gp) => gp?.buyerInvoice?.invoicePaymentStatus?.slug !== "cancelled"
//   )
// );
      } catch (error) {
        console.error("Error fetching gate passes:", error);
        setError("Failed to load gate passes. Please try again.");
        setGatePasses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGatePasses();
  }, [user?.id, currentPage, pageSize, debouncedSearch, filterStatus, selectedBuyerId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getGatePassStatus = (gatePass, translateFn) => {
    const currentDate = new Date();
    const expiryDate = new Date(gatePass.validTillDate);
    const daysUntilExpiry = Math.ceil(
      (expiryDate - currentDate) / (1000 * 60 * 60 * 24),
    );
    if (daysUntilExpiry < 0) {
      return {
        text: translateFn("historyGatePass.status.expired"),
        color: "text-white bg-red-700",
        icon: <XCircle className="w-4 h-4" />,
      };
    }
    return {
      text: translateFn("historyGatePass.status.valid"),
      color: "text-white bg-green-600",
      icon: <CheckCircle className="w-4 h-4" />,
    };
  };

  const handleDownloadGatePass = async (gatePass) => {
    // try {
    //   setDownloadingId(gatePass?.id);
    //   const blob = await downloadGatePassPdf(gatePass?.id);
    //   const href = URL.createObjectURL(blob);
    //   const a = document.createElement("a");
    //   a.href = href;
    //   a.download = `gatepass-${gatePass?.gatePassNumber || gatePass?.id}.pdf`;
    //   document.body.appendChild(a);
    //   a.click();
    //   a.remove();
    //   setTimeout(() => URL.revokeObjectURL(href), 10000);
    // } catch (error) {
    //   console.error("Error downloading gate pass PDF:", error);
    //   alert("Failed to download gate pass PDF. Please try again.");
    // } finally {
    //   setDownloadingId(null);
    // }
  const status = getGatePassStatus(gatePass, translate);

    generateGatePassPdf({
      gatePass,
      translate,
      assets: { skyautologo, statusPng: status.text === translate("historyGatePass.status.expired") ? expirePng : approvedpng },
      status
    });
  };

  const handleViewGatePass = (gatePass) => {
  const status = getGatePassStatus(gatePass, translate);

    generateGatePassPdf({
      gatePass,
      translate,
      assets: { skyautologo, statusPng: status.text === translate("historyGatePass.status.expired") ? expirePng : approvedpng },
      status,
      outputMode: 'view'
    });
  };

  const handleCloseModal = () => {
    setShowGatePassModal(false);
    setSelectedGatePass(null);
  };

  const mapGatePassData = (gatePass) => {
    if (!gatePass) return {};

    return {
      gateNo: gatePass.gatePassNumber || gatePass.id || "-",
      issuedDate: formatDate(gatePass.issueDate || gatePass.createdAt) || "-",
      custName: user?.name || gatePass?.customerName || "-",
      custEmail: user?.email || gatePass?.customerEmail || "",
      custPhone: user?.mobileNo || gatePass?.customerPhone || "-",
      handOverTo: "Customer",
      recoveryName: "-",
      recoveryPhone: "-",
      invNo: gatePass?.invoiceNumber || gatePass?.id || "-",
      vinNo: gatePass?.vehicle?.vinNumber || gatePass?.vinNumber || "-",
      make: gatePass?.vehicle?.make || gatePass?.make || "-",
      model: gatePass?.vehicle?.model || gatePass?.model || "-",
      year: gatePass?.vehicle?.year || gatePass?.year || "-",
      color: gatePass?.vehicle?.color || gatePass?.color || "-",
      auctionDate: formatDate(gatePass?.auctionDate) || "-",
      hammer: displayPriceWithSymbol(
        convertPrice(Number(gatePass?.hammerPrice || gatePass?.price || 0)),
      ),
      total: displayPriceWithSymbol(
        convertPrice(Number(gatePass?.totalAmount || gatePass?.price || 0)),
      ),
      paymentRef: gatePass?.paymentReference || "-",
      verifyUrl: `https://khataljazeera.com/verify?no=${gatePass.gatePassNumber || gatePass.id}`,
    };
  };

  const getChildBuyerName = (gatePass) => {
    const userObj = gatePass?.buyerInvoice?.buyer?.user;
    if (!userObj) return "—";
    return userObj.clientId ? `${userObj.name} (${userObj.clientId})` : (userObj.name || "—");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48 sm:h-64">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="text-red-600 mb-3 sm:mb-4 text-sm sm:text-base">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-white rounded-[6px] sm:rounded-lg hover:bg-primary-dark text-xs sm:text-sm font-medium transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const isRTL = language === "ar";

  return (
    <div
      className="bg-card rounded-lg sm:rounded-xl shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]">
          <div className="flex flex-col flex-1">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {translate("historyGatePass.heading")}
            </h2>
            {/* {selectedBuyerDisplayName && (
              <p className="text-xs text-white/80 mt-1">
                Showing for {selectedBuyerDisplayName}
                {selectedBuyerDisplayClientId ? ` (${selectedBuyerDisplayClientId})` : ""}
              </p>
            )} */}
            <span className="text-[10px] sm:text-xs font-medium text-white/60 mt-1">
              {translate("historyGatePass.totalGatePasses")}:{" "}
              {pagination?.totalItems || 0}
            </span>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto">
            {/* Child Buyer Dropdown */}
            {childBuyers.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedBuyerId || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedBuyerId(val ? Number(val) : null);
                    setCurrentPage(1);
                  }}
                  className="bg-page p-2 rounded-full text-white text-xs focus:outline-none"
                >
                  {/* Default "All" option selected initially */}
                  <option value="">Select Buyer</option>
                  {childBuyers.map((child) => (
                    <option key={child.buyerId} value={child.buyerId} className="text-white">
                      {child.user?.name || child.businessName || "-"}
                      {child.user?.clientId ? ` (${child.user.clientId})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="relative w-full md:w-60 xl:w-72">
              <input
                type="text"
                placeholder={translate("historyGatePass.searchPlaceholder")}
                className={`w-full px-3 py-1.5 ${isRTL ? "pr-9" : "pl-9"} bg-black/10 border border-white/10 rounded-full text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/30 text-xs transition-all`}
                value={searchTerm || ""}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-white/70`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            <div className="flex items-center overflow-x-auto no-scrollbar">
              <nav
                className={`flex gap-1.5 sm:gap-2 ${isRTL ? "flex-row-reverse" : ""}`}
              >
                {[
                  {
                    key: "all",
                    label: translate("historyGatePass.filters.all"),
                  },
                  {
                    key: "valid",
                    label: translate("historyGatePass.filters.valid"),
                  },
                  {
                    key: "expired",
                    label: translate("historyGatePass.filters.expired"),
                  },
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setFilterStatus(filter.key)}
                    className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-full font-medium text-[10px] sm:text-xs transition-all duration-200 ${filterStatus === filter.key
                      ? "bg-white text-primary shadow-md"
                      : "bg-black/10 text-white/80 hover:bg-white/10"
                      }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4">
        <div className="space-y-2 sm:space-y-3">
          {gatePasses.length === 0 ? (
            <div className="text-center py-8 sm:py-12 px-4">
              <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-white sm:text-lg font-medium mb-2">
                {translate("historyGatePass.messages.noGatePassesFound")}
              </h3>
              <p className="text-sm sm:text-base text-description">
                {filterStatus === "all"
                  ? translate("historyGatePass.messages.noGatePassesYet")
                  : translate(
                    "historyGatePass.messages.noFilteredGatePasses",
                  ).replace(
                    "{status}",
                    translate(`historyGatePass.filters.${filterStatus}`),
                  )}
              </p>
            </div>
          ) : (
            <>
              {gatePasses.map((gatePass) => {
                const status = getGatePassStatus(gatePass, translate);
                const isChildRecord = !!gatePass.buyerInvoice?.buyer?.parentId;

                return (
                  <div
                    key={gatePass.id}
                    className={`${isChildRecord ? "bg-gray-900/40" : "bg-bg-medium"} border border-gray-800/60 rounded-xl p-5 md:p-6 hover:shadow-xl hover:shadow-black/30 transition-all duration-200`}
                  >
                    {/* MOBILE LAYOUT */}
                    <div className="block lg:hidden space-y-5">
                      {/* Header + Status */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          <h3 className="text-base font-semibold text-gray-100 truncate">
                            {translate("historyGatePass.labels.gatePassNumber")}{" "}
                            {gatePass.gatePassNumber || gatePass.id}
                          </h3>
                        </div>

                        <span
                          className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 flex-shrink-0 ${status.color}`}
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            {status.icon}
                          </span>
                          <span>{status.text}</span>
                        </span>
                      </div>

                      {/* Vehicle image + basic info */}
                      <div className="flex gap-4">
                        <div className="w-28 h-28 bg-bg-medium rounded-xl overflow-hidden flex-shrink-0 border border-gray-800/50">
                          {gatePass?.vehicle?.images?.[0]?.imageUrl ? (
                            <img
                              src={
                                gatePass.vehicle.images[0].imageUrl ||
                                placeholder
                              }
                              alt="Vehicle"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-bg-light flex items-center justify-center text-gray-600 text-sm">
                              No image
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0 space-y-1.5">
                          <p className="text-base font-semibold text-gray-100 leading-tight">
                            {gatePass?.vehicle?.name ||
                              gatePass?.vehicleName ||
                              "—"}
                          </p>
                          <p className="text-sm text-gray-400">
                            Lot #{" "}
                            {gatePass?.vehicle?.lotNumber ||
                              gatePass?.lotNumber ||
                              "—"}
                          </p>
                          <p className="text-xl font-bold text-blue-400 pt-1">
                            {displayPriceWithSymbol(
                              convertPrice(
                                Number(
                                  gatePass?.vehicle?.soldPrice ||
                                  gatePass?.soldPrice ||
                                  0,
                                ),
                              ),
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Issue & Expiry Dates */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-bg-medium rounded-xl p-4 border border-gray-800/50">
                          <p className="text-xs text-gray-400 mb-1.5">
                            {translate("historyGatePass.labels.issueDate")}
                          </p>
                          <p className="text-sm font-medium text-gray-200">
                            {formatDate(
                              gatePass?.issueDate || gatePass?.createdAt,
                            )}
                          </p>
                        </div>

                        <div className="bg-bg-medium rounded-xl p-4 border border-gray-800/50">
                          <p className="text-xs text-gray-400 mb-1.5">
                            {translate("historyGatePass.labels.expiryDate")}
                          </p>
                          <p className="text-sm font-medium leading-relaxed">
                            {(() => {
                              const validTillDays = gatePass?.validTillDate;
                              const {
                                expiryDateStr,
                                dayOfWeek,
                                daysRemaining,
                                color,
                              } = getExpiryInfo(validTillDays);

                              return (
                                <span style={{ color }}>
                                  {expiryDateStr} ({dayOfWeek})
                                  {typeof daysRemaining === "number" && (
                                    <span
                                      className={`${isRTL ? "mr-1.5" : "ml-1.5"} text-xs block mt-1 opacity-90`}
                                    >
                                      {daysRemaining}{" "}
                                      {translate(
                                        "historyGatePass.labels.daysLeft",
                                      )}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-row sm:flex-row gap-3">
                        <button
                          onClick={() => handleViewGatePass(gatePass)}
                          disabled={streamingId === gatePass?.id}
                          className="flex-1 flex items-center justify-center gap-2 p-2 bg-blue-700 hover:bg-blue-600 text-white rounded-xl  font-medium "
                        >
                          {streamingId === gatePass?.id ? (
                            <Loader className="w-5 h-5 animate-spin" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                          <span>
                            {translate("historyGatePass.buttons.view")}
                          </span>
                        </button>

                        <button
                          onClick={() => handleDownloadGatePass(gatePass)}
                          disabled={downloadingId === gatePass?.id}
                          className={`flex-1 flex items-center justify-center gap-2 pl-2 py-2 bg-bg-medium border border-gray-700 hover:bg-gray-800 text-gray-100 rounded-xl transition-colors font-medium disabled:opacity-60 shadow-sm ${isRTL ? "flex-row-reverse" : ""}`}
                        >
                          {downloadingId === gatePass?.id ? (
                            <Loader
                              className={`w-5 h-5 ${isRTL ? "ml-0" : "mr-0"} animate-spin`}
                            />
                          ) : (
                            <Download
                              className={`w-5 h-5 ${isRTL ? "ml-2" : "mr-2"}`}
                            />
                          )}
                          <span>
                            {translate("historyGatePass.buttons.download")}
                          </span>
                        </button>
                      </div>

                      {/* Collapsible Details */}
                      <details className="bg-bg-medium rounded-xl border border-gray-800/50">
                        <summary className="px-4 py-3 font-medium text-gray-100 cursor-pointer flex items-center justify-between text-sm">
                          <span>
                            {translate(
                              "historyGatePass.labels.gatePassDetails",
                            )}
                          </span>
                          <span className="text-gray-500 text-xs">▼</span>
                        </summary>
                        <div className="px-4 pb-4 pt-2 space-y-4 text-sm border-t border-gray-800/60">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                            <span
                              className={`text-gray-400 font-medium sm:w-32 ${isRTL ? "sm:ml-0" : "sm:mr-0"}`}
                            >
                              {translate("historyGatePass.labels.vinNumber")}:
                            </span>
                            <span className="font-medium text-gray-200 break-all">
                              {gatePass?.vehicle?.vinNumber
                                ? "************" +
                                gatePass.vehicle.vinNumber.slice(-5)
                                : "—"}
                            </span>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                            <span
                              className={`text-gray-400 font-medium sm:w-32 ${isRTL ? "sm:ml-0" : "sm:mr-0"}`}
                            >
                              {translate("historyGatePass.labels.auction")}:
                            </span>
                            <span className="font-medium text-gray-200 break-words">
                              {gatePass?.buyerInvoice?.buyerInvoiceDetails?.[0]
                                ?.auctionLane?.auction?.name || "N/A"}
                            </span>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                            <span
                              className={`text-gray-400 font-medium sm:w-32 ${isRTL ? "sm:ml-0" : "sm:mr-0"}`}
                            >
                              {translate("historyGatePass.labels.location")}:
                            </span>
                            <span className="font-medium text-gray-200 break-words">
                              {gatePass?.location ||
                                gatePass?.pickupLocation ||
                                "N/A"}
                            </span>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                            <span
                              className={`text-gray-400 font-medium sm:w-32 ${isRTL ? "sm:ml-0" : "sm:mr-0"}`}
                            >
                              {translate("historyGatePass.labels.instructions")}
                              :
                            </span>
                            <span className="font-medium text-gray-200 flex-1">
                              {gatePass?.instructions ||
                                translate(
                                  "historyGatePass.messages.defaultInstructions",
                                )}
                            </span>
                          </div>
                        </div>
                      </details>
                    </div>

                    {/* DESKTOP LAYOUT */}
                    <div className="hidden lg:block">
                      <div className="flex items-start justify-between gap-6">
                        {/* Left – main content */}
                        <div className="flex-1 space-y-5">
                          {/* Header */}
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-gray-500" />
                              <h3 className="text-lg font-semibold text-gray-100">
                                {translate(
                                  "historyGatePass.labels.gatePassNumber",
                                )}{" "}
                                {gatePass.gatePassNumber || gatePass.id}
                              </h3>
                            </div>

                            <span
                              className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${status.color}`}
                            >
                              <span className="w-4 h-4 flex items-center justify-center">
                                {status.icon}
                              </span>
                              <span>{status.text}</span>
                            </span>
                          </div>

                          {/* Vehicle row + dates + price */}
                          <div className="bg-bg-medium rounded-xl p-5 border border-gray-800/50">
                            <div className="flex items-center gap-6">
                              {/* Image */}
                              <div className="w-20 h-20 bg-bg-light rounded-lg overflow-hidden flex-shrink-0 border border-gray-800/50">
                                {gatePass?.vehicle?.images?.[0]?.imageUrl ? (
                                  <img
                                    src={
                                      gatePass.vehicle.images[0].imageUrl ||
                                      placeholder
                                    }
                                    onError={(e) =>
                                      (e.target.src = placeholder)
                                    }
                                    alt="Vehicle"
                                    className="w-full h-full object-cover"
                                  />
                                ) : null}
                              </div>

                              {/* Name + Lot */}
                              <div className="flex-1">
                                <p className="font-semibold text-gray-100">
                                  {gatePass?.vehicle?.name ||
                                    gatePass?.vehicleName ||
                                    "—"}
                                </p>
                                <p className="text-sm text-gray-400 mt-0.5">
                                  Lot #{" "}
                                  {gatePass?.vehicle?.lotNumber ||
                                    gatePass?.lotNumber ||
                                    "—"}
                                </p>
                                <div>
                                  <span className="text-gray-400">
                                    {translate(
                                      "wonVehicles.labels.childName",
                                    ) || "Buyer"}
                                    :
                                  </span>
                                  <span className="font-medium text-gray-200">
                                    {getChildBuyerName(gatePass)}
                                  </span>
                                </div>
                              </div>

                              {/* Issue Date */}
                              <div className="min-w-[140px]">
                                <p className="text-xs text-gray-400 mb-1">
                                  {translate(
                                    "historyGatePass.labels.issueDate",
                                  )}
                                </p>
                                <p className="font-medium text-gray-200">
                                  {formatDate(
                                    gatePass?.issueDate || gatePass?.createdAt,
                                  )}
                                </p>
                              </div>

                              {/* Expiry Date */}
                              <div className="min-w-[180px]">
                                <p className="text-xs text-gray-400 mb-1">
                                  {translate(
                                    "historyGatePass.labels.expiryDate",
                                  )}
                                </p>
                                <p className="font-medium leading-relaxed">
                                  {(() => {
                                    const validTillDays =
                                      gatePass?.validTillDate;
                                    const {
                                      expiryDateStr,
                                      dayOfWeek,
                                      daysRemaining,
                                      color,
                                    } = getExpiryInfo(validTillDays);
                                    return (
                                      <span style={{ color }}>
                                        {expiryDateStr} ({dayOfWeek})
                                        {typeof daysRemaining === "number" && (
                                          <span className="text-xs block mt-1 opacity-90">
                                            {daysRemaining}{" "}
                                            {translate(
                                              "historyGatePass.labels.daysLeft",
                                            )}
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })()}
                                </p>
                              </div>

                              {/* Price */}
                              <div className="min-w-[140px] text-right">
                                <p className="text-xl font-bold text-blue-400">
                                  {displayPriceWithSymbol(
                                    convertPrice(
                                      Number(
                                        gatePass?.vehicle?.soldPrice ||
                                        gatePass?.soldPrice ||
                                        0,
                                      ),
                                    ),
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Details grid */}
                          <div className="bg-bg-medium rounded-xl p-5 border border-gray-800/50">
                            <h4 className="font-medium text-gray-100 mb-4 text-base">
                              {translate(
                                "historyGatePass.labels.gatePassDetails",
                              )}
                            </h4>
                            <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
                              <div>
                                <span className="text-gray-400">
                                  {translate(
                                    "historyGatePass.labels.vinNumber",
                                  )}
                                  :{" "}
                                </span>
                                <span className="font-medium text-gray-200">
                                  {gatePass?.vehicle?.vinNumber
                                    ? "************" +
                                    gatePass.vehicle.vinNumber.slice(-5)
                                    : "—"}
                                </span>
                              </div>

                              <div>
                                <span className="text-gray-400">
                                  {translate("historyGatePass.labels.auction")}
                                  :{" "}
                                </span>
                                <span className="font-medium text-gray-200">
                                  {gatePass?.buyerInvoice
                                    ?.buyerInvoiceDetails?.[0]?.auctionLane
                                    ?.auction?.name || "N/A"}
                                </span>
                              </div>

                              <div>
                                <span className="text-gray-400">
                                  {translate("historyGatePass.labels.location")}
                                  :{" "}
                                </span>
                                <span className="font-medium text-gray-200">
                                  {gatePass?.location ||
                                    gatePass?.pickupLocation ||
                                    "N/A"}
                                </span>
                              </div>

                              <div>
                                <span className="text-gray-400">
                                  {translate(
                                    "historyGatePass.labels.instructions",
                                  )}
                                  :{" "}
                                </span>
                                <span className="font-medium text-gray-200">
                                  {gatePass?.instructions ||
                                    translate(
                                      "historyGatePass.messages.defaultInstructions",
                                    )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right – actions */}
                        <div className="flex flex-row gap-3 ">
                          <div className="relative h-10 w-10">
                            <button
                              onClick={() => handleViewGatePass(gatePass)}
                              disabled={streamingId === gatePass?.id}
                              className="absolute right-0 top-0 h-10 w-10 hover:w-36 flex items-center bg-primary border border-blue-700 hover:bg-primary-hover text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group disabled:opacity-60"
                            >
                              {/* Wrapper with fixed layout */}
                              <div className="flex items-center h-full w-full">
                                {/* Icon Container: Always centered. Shows Loader or Eye */}
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                  {streamingId === gatePass?.id ? (
                                    <Loader
                                      size={20}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <Eye size={20} /> // Using Ticket icon for Gate Pass, or keep Eye
                                  )}
                                </div>

                                {/* Text: Slides out */}
                                <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                  {translate("historyGatePass.buttons.view")}
                                </span>
                              </div>
                            </button>
                          </div>

                          <div className="relative h-10 w-10">
                            <button
                              onClick={() => handleDownloadGatePass(gatePass)}
                              disabled={downloadingId === gatePass?.id}
                              className="absolute right-0 top-0 h-10 w-10 hover:w-44 flex items-center bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-100 rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group disabled:opacity-60"
                            >
                              {/* Wrapper for layout & RTL support */}
                              <div
                                className={`flex items-center h-full w-full ${isRTL ? "flex-row-reverse" : ""}`}
                              >
                                {/* Icon Container: Loader or Download Icon */}
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                  {downloadingId === gatePass?.id ? (
                                    <Loader
                                      size={20}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <Download size={20} />
                                  )}
                                </div>

                                {/* Text: Slides out on hover */}
                                <span className="px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                  {translate(
                                    "historyGatePass.buttons.download",
                                  )}
                                </span>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {pagination?.totalPages > 1 && (
                <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex justify-center sm:justify-end">
                  <Pagination
                    totalPages={pagination.totalPages}
                    currentPage={pagination.currentPage}
                    setCurrentPage={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Gate Pass Modal */}
      {showGatePassModal && selectedGatePass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-3 md:p-4">
          <div className="bg-white rounded-[8px] sm:rounded-lg shadow-xl max-w-[650px] w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-3 sm:p-4">
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                {translate("historyGatePass.labels.vehicleOutPass")}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            <div className="p-2 sm:p-3 md:p-4">
              <GatePassFromImage data={mapGatePassData(selectedGatePass)} />
            </div>
            <div className="flex justify-end p-3 sm:p-4 border-t">
              <button
                onClick={handleCloseModal}
                className="px-4 sm:px-6 py-1.5 sm:py-2 bg-primary text-white rounded-[6px] sm:rounded-lg hover:bg-primary-dark transition-colors text-xs sm:text-sm font-medium"
              >
                {translate("historyGatePass.buttons.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyGatePasses;
